import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { Spinner } from '../Spinner';
import { AlertRow } from '../shell/AlertRow';
import { formatNumber } from '../../utils/format';
import { resolveMaxFill } from '../../constants/aid';
import { COLORS } from '../../constants/theme';
import type { AidShellProps } from '../../pages/Aid';
import type { ResourceAmounts } from '../../types/aid';

const RESOURCE_ORDER: (keyof ResourceAmounts)[] = ['gold', 'food', 'soldiers', 'runes'];

const RESOURCE_LABELS: Record<keyof ResourceAmounts, string> = {
  gold: 'Gold',
  food: 'Food',
  soldiers: 'Soldiers',
  runes: 'Runes',
};

/**
 * SF-154: thin wrapper around the existing (already desktop-appropriate) Aid JSX — no layout
 * redesign. Adds a real, focus-managed `<h1>`, `flex-wrap` on the stat-card row (fixes a defect
 * the Overview names — preserving it verbatim on desktop while fixing it on mobile would be an
 * unstated asymmetry), the same `kingdomLoadError`/empty-kingdom `AlertRow` pair mobile gets, a
 * small text Max button per resource input (`DesktopExploreShell.tsx:117-130` precedent), and
 * `submitDisabledReason` rendered where `submitError` used to be the sole inline message —
 * everything else is unchanged from pre-SF-154 `Aid.tsx`.
 */
export function DesktopAidShell({
  province,
  availableResources,
  resourceAmounts,
  handleResourceChange,
  handleMaxFill,
  selectedTarget,
  setSelectedTarget,
  kingdomMembers,
  isLoadingKingdom,
  kingdomLoadError,
  loadKingdom,
  tradeValue,
  maxShipment,
  estimatedTaxInfo,
  hasResources,
  isOverLimit,
  isApproachingLimit,
  canSubmit,
  submitDisabledReason,
  isSubmitting,
  submitError,
  handleSendAid,
  aidResult,
}: AidShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  const tradeBalance = province.resources.tradeBalance;

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Aid
      </h1>

      {/* Stat Cards */}
      <div className="flex flex-wrap gap-3">
        <div className="w-36">
          <StatCard
            label="Gold"
            value={`${formatNumber(availableResources.gold)}gc`}
            color={COLORS.gold}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Food"
            value={formatNumber(availableResources.food)}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Soldiers"
            value={formatNumber(availableResources.soldiers)}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Runes"
            value={formatNumber(availableResources.runes)}
            borderless
          />
        </div>
        <div className="w-40">
          <StatCard
            label="Trade Balance"
            value={`${tradeBalance >= 0 ? '+' : ''}${formatNumber(Math.round(tradeBalance))}gc`}
            color={tradeBalance >= 0 ? COLORS.positive : COLORS.negative}
            borderless
          />
        </div>
      </div>

      {/* Aid Form */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        {/* Target + Resources input section */}
        <div className="p-4 space-y-3" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          {/* Target selection */}
          <div className="flex items-center gap-2">
            <span className="text-sm w-20 text-right" style={{ color: COLORS.text.tertiary }}>Recipient</span>
            {isLoadingKingdom ? (
              <div className="flex items-center gap-2">
                <Spinner size="sm" />
                <span className="text-sm" style={{ color: COLORS.text.tertiary }}>Loading...</span>
              </div>
            ) : (
              <select
                aria-label="Recipient"
                value={selectedTarget}
                onChange={(e) => setSelectedTarget(e.target.value)}
                className="rounded px-3 py-1.5 text-sm"
                style={{
                  backgroundColor: COLORS.inputBg,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: COLORS.inputBorder,
                  color: COLORS.text.primary,
                }}
              >
                <option value="">Select a province...</option>
                {kingdomMembers.map((member) => (
                  <option key={member.provinceId} value={member.provinceId}>
                    {member.provinceName} ({member.location})
                  </option>
                ))}
              </select>
            )}
          </div>

          {kingdomLoadError ? (
            <div className="space-y-2">
              <AlertRow severity="critical" message={kingdomLoadError} />
              <button
                onClick={() => loadKingdom()}
                className="text-sm px-2.5 py-1 rounded"
                style={{ color: COLORS.link, border: `1px solid ${COLORS.link}`, background: 'none' }}
              >
                Retry
              </button>
            </div>
          ) : !isLoadingKingdom && kingdomMembers.length === 0 ? (
            <AlertRow severity="warning" message="Your kingdom has no other members to send aid to." />
          ) : null}

          {/* Resource inputs — one per row, labels aligned */}
          {RESOURCE_ORDER.map((resource) => {
            const available = availableResources[resource];
            const maxFill = resolveMaxFill(resource, resourceAmounts, available, maxShipment);
            const label = RESOURCE_LABELS[resource];
            return (
              <div key={resource} className="flex items-center gap-2">
                <span className="text-sm w-20 text-right capitalize" style={{ color: COLORS.text.tertiary }}>
                  {resource}
                </span>
                <input
                  type="number"
                  min="0"
                  max={available}
                  value={resourceAmounts[resource] || ''}
                  onChange={(e) => handleResourceChange(resource, parseInt(e.target.value, 10) || 0)}
                  onFocus={(e) => e.target.select()}
                  placeholder="0"
                  aria-label={`${label} amount`}
                  className="rounded px-3 py-1.5 text-sm w-28"
                  style={{
                    backgroundColor: COLORS.inputBg,
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: COLORS.inputBorder,
                    color: COLORS.text.primary,
                  }}
                />
                <button
                  onClick={() => handleMaxFill(resource)}
                  disabled={maxFill === 0}
                  aria-label={`Max ${label}`}
                  className="text-xs px-2 py-1 rounded disabled:opacity-50"
                  style={{ color: COLORS.link, border: `1px solid ${COLORS.link}`, background: 'none' }}
                >
                  Max
                </button>
                <span className="text-xs whitespace-nowrap" style={{ color: COLORS.text.muted }}>
                  / {formatNumber(available)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Summary + Actions */}
        <div className="px-4 py-3 space-y-3">
          {/* Messages — submitDisabledReason takes priority over submitError when both apply */}
          {submitDisabledReason ? (
            <div className="text-sm" style={{ color: COLORS.warning }}>{submitDisabledReason}</div>
          ) : submitError ? (
            <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
          ) : null}
          {isOverLimit && (
            <div className="text-sm" style={{ color: COLORS.negative }}>
              Shipment exceeds maximum allowed ({formatNumber(maxShipment)}gc). Reduce resources.
            </div>
          )}
          {isApproachingLimit && !isOverLimit && (
            <div className="text-sm" style={{ color: COLORS.warning }}>
              Approaching shipment limit (90% of max)
            </div>
          )}

          {/* Success Result */}
          {aidResult && aidResult.success && (
            <div
              className="rounded p-3 space-y-2 text-sm"
              role="status"
              style={{
                backgroundColor: COLORS.successBg,
                border: `1px solid ${COLORS.successBorder}`,
              }}
            >
              <p className="font-semibold" style={{ color: COLORS.successText }}>
                {aidResult.message}
              </p>
              <div className="grid grid-cols-2 gap-2" style={{ color: COLORS.text.secondary }}>
                <div>Shipment Value: {formatNumber(Math.round(aidResult.shipmentValue))}gc</div>
                <div>Tax Applied: {(aidResult.taxRate * 100).toFixed(1)}%</div>
              </div>
              {aidResult.taxRate > 0 && (
                <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
                  Recipient received: {formatNumber(aidResult.receivedGold)}gc,{' '}
                  {formatNumber(aidResult.receivedFood)} food,{' '}
                  {formatNumber(aidResult.receivedSoldiers)} soldiers,{' '}
                  {formatNumber(aidResult.receivedRunes)} runes
                </div>
              )}
              <div
                className="pt-2"
                style={{
                  borderTop: `1px solid ${COLORS.successBorder}`,
                  color: COLORS.text.secondary,
                }}
              >
                Your new trade balance:{' '}
                <span style={{ color: aidResult.senderTradeBalance >= 0 ? COLORS.positive : COLORS.negative }}>
                  {aidResult.senderTradeBalance >= 0 ? '+' : ''}
                  {formatNumber(Math.round(aidResult.senderTradeBalance))}gc
                </span>
              </div>
            </div>
          )}

          {/* Failure Result */}
          {aidResult && !aidResult.success && (
            <div className="text-sm" role="alert" style={{ color: COLORS.negative }}>
              {aidResult.message}
            </div>
          )}

          {/* Summary row */}
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Trade Value: </span>
              <span className="font-mono" style={{ color: isOverLimit ? COLORS.negative : tradeValue > 0 ? COLORS.gold : COLORS.text.tertiary }}>
                {formatNumber(Math.round(tradeValue))}gc
              </span>
            </span>
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Max Shipment: </span>
              <span className="font-mono" style={{ color: COLORS.text.primary }}>
                ~{formatNumber(maxShipment)}gc
              </span>
            </span>
            {estimatedTaxInfo && hasResources && (
              <span className="whitespace-nowrap">
                <span style={{ color: COLORS.text.tertiary }}>Estimated tax: </span>
                <span className="font-mono" style={{ color: estimatedTaxInfo.taxRate > 0 ? COLORS.warning : COLORS.text.tertiary }}>
                  {(estimatedTaxInfo.taxRate * 100).toFixed(1)}%
                </span>
              </span>
            )}
          </div>
          {estimatedTaxInfo && hasResources && (
            <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
              Recipient receives ~{formatNumber(estimatedTaxInfo.receivedGold)}gc,{' '}
              {formatNumber(estimatedTaxInfo.receivedFood)} food,{' '}
              {formatNumber(estimatedTaxInfo.receivedSoldiers)} soldiers,{' '}
              {formatNumber(estimatedTaxInfo.receivedRunes)} runes
            </div>
          )}

          {/* Actions row */}
          <div className="flex items-center">
            <div className="text-xs" style={{ color: COLORS.text.tertiary }}>
              Values: Gold=1gc, Food=0.05gc, Soldiers=100gc, Runes=3gc
            </div>
            <div className="flex gap-2 ml-auto">
              <Button
                onClick={handleSendAid}
                isLoading={isSubmitting}
                disabled={!canSubmit}
              >
                Send Aid
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
